import type { AnalyticsTab } from "@lafayette/schemas";
import { Link } from "react-router";

import { cn } from "../../ui/cn";
import { Icon, type IconName } from "../../ui/icon";
import { countText } from "../format";

// The tabs of the cockpit: Overview, Explore and Events. A tab is a link whose address keeps the customer, the tab and the range, so a link, a
// reload and the back button hold the view. The current tab has `aria-current="page"`. The bar has `min-w-0`: the page is a grid, and without it the
// three tabs set the width of the whole page (402 px at 390 px of window width); now the list of tabs scrolls inside the bar.

const TABS: readonly Readonly<{ key: AnalyticsTab; label: string; icon: IconName }>[] = [
  { key: "overview", label: "Overview", icon: "dashboard-speed-01" },
  { key: "explore", label: "Explore", icon: "zoom-in-area" },
  { key: "events", label: "Events", icon: "note-01" },
];

// The hint at the right end of the bar says what a click or a drag does on the tab that is open.
const HINTS: Readonly<Record<AnalyticsTab, string>> = {
  overview: "Click any number or chart to zoom in",
  explore: "Drag across the chart to zoom further · Esc goes back",
  events: "Read what the customer did in the app, in order",
};

export function TabBar({
  current,
  hrefOf,
  eventCount = null,
}: Readonly<{
  current: AnalyticsTab;
  hrefOf: (tab: AnalyticsTab) => string;
  /** The number of events of the customer in the range (CHT-2030), for the label of the Events tab. `null`: not known, nothing is drawn. */
  eventCount?: number | null;
}>) {
  return (
    <nav
      aria-label="Analytics tabs"
      className="flex min-w-0 items-end justify-between gap-4 border-b border-hairline"
    >
      <ul className="m-0 flex list-none gap-1 compact:gap-2 overflow-x-auto p-0">
        {TABS.map((tab) => {
          const active = tab.key === current;
          return (
            <li key={tab.key}>
              <Link
                to={hrefOf(tab.key)}
                replace
                aria-current={active ? "page" : undefined}
                className={cn(
                  "-mb-px inline-flex min-h-11 items-center gap-2 border-b-2 px-3.5 text-sm no-underline compact:min-h-8",
                  active
                    ? "border-primary font-semibold text-link"
                    : "border-transparent font-medium text-muted-foreground hover:text-foreground",
                )}
              >
                <Icon name={tab.icon} size="sm" />
                {tab.label}
                {tab.key === "events" && eventCount !== null ? (
                  <span className="rounded-full bg-pill px-2 py-0.5 text-xs font-semibold text-muted-foreground tabular-nums">
                    {countText(eventCount)}
                  </span>
                ) : null}
              </Link>
            </li>
          );
        })}
      </ul>
      <p className="m-0 hidden items-center gap-1.5 pb-2 text-[13px] text-muted-foreground md:flex">
        <Icon name="zoom-in-area" size="sm" />
        {HINTS[current]}
      </p>
    </nav>
  );
}
